'use client';

import Tv from 'icons/lib/404tv.svg';
import Screen from 'icons/lib/screen.svg';
import Link from 'next/link';
import { useEffect, useRef } from 'react';
import { Button } from 'ui';

import { useTranslation } from '@/i18n/client';
import { dataLayer } from '@/modules/google/data-layer/lib/data-layer';

import styles from './NotFound.module.scss';

export const NotFound = () => {
  const { t } = useTranslation(['error', 'common']);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    dataLayer({
      event: 'error_page_view',
      error_code: '404',
      page_type: 'error',
    });
    if (!containerRef.current) {
      return;
    }

    // This is workaround for sidebar styles on 404 page that seem broken in current version of Next.js.
    // Styles for this page are loaded late and when they eventually do load, a lot of layout shifts happen.
    // Without this sidebar animation is fired which shows sidebar closing on page load.
    // Here sidebars have inline styles to hide them, and when component is ready they are then displayed again.
    // At this point sidebars are outside the viewport, so it looks nicer.
    containerRef.current.style.display = 'inherit';
  }, []);

  return (
    <div
      style={{ display: 'none' }}
      ref={containerRef}
      className={styles.notFound}
    >
      <div className="container">
        <span className={styles['notFound--errorTitle']}>
          {t('error.title')}
        </span>
        <div className={styles.notFound__svgPlace}>
          <Tv className={styles['notFound__svgPlace--tv']} />
        </div>
        <div className={styles.notFound__screenPlace}>
          <Screen className={styles['notFound__screenPlace--screenSvg']} />
        </div>
        <h1 className={styles['notFound--title']}>{t('notFound.title')}</h1>
        <p className={styles['notFound--description']}>
          {t('notFound.description')}
        </p>
        <Button
          buttonName="backButton"
          variant="blue"
          as={Link}
          prefetch={false}
          href="/"
        >
          {t('common:common.goToHomePage')}
        </Button>
      </div>
    </div>
  );
};
